import React, { useState, useEffect } from 'react';
import { Card, Form, Select, DatePicker, Button, Input, message, Layout, Typography, InputNumber, Divider, Row, Col, Alert } from 'antd';
import { LogoutOutlined, MedicineBoxOutlined, ShopOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getAssignedPersons } from '../../redux/slices/persons/personsThunk'; 
import { addRapport } from '../../redux/slices/rapports/rapportsThunk';
import { clearToken } from '../../redux/sign-in/signinSlice';
import apiClient from '../../services/apiClient'; 
// --- 1. AJOUT DE L'IMPORT DAYJS ---
import dayjs from 'dayjs'; 

const { Header, Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

const EmergencyReportPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [form] = Form.useForm();
  const user = useSelector((state) => state.signin.user);
  
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [personType, setPersonType] = useState(1); 
  
  const { assignedPersons, loading: loadingPersons } = useSelector((state) => state.persons);
  const [productsList, setProductsList] = useState([]);
  const [samplesList, setSamplesList] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);

  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    if (user?.delegateId) {
      dispatch(getAssignedPersons({ 
          delegateId: user.delegateId, 
          page: 1, 
          limit: 1000, 
          filter: { type: [personType] } 
      }));
    }
  }, [dispatch, user, personType]);

  useEffect(() => {
      if (user?.delegateId) {
          fetchItems();
      }
  }, [user]);

  const fetchItems = async () => {
    setLoadingItems(true);
    try {
      const [productsRes, samplesRes] = await Promise.all([
          apiClient.get(`/api/v1/products?limit=1000`), 
          apiClient.get(`/api/v1/samples?limit=1000`)
      ]);
      const products = productsRes.data?.data || productsRes.data || [];
      const samples = samplesRes.data?.data || samplesRes.data || [];
      setProductsList(products);
      setSamplesList(samples);
    } catch (error) {
      console.error("Erreur chargement produits/échantillons", error);
    } finally {
        setLoadingItems(false);
    }
  };

  // --- 2. FONCTION POUR BLOQUER LES DATES FUTURES ---
  const disabledDate = (current) => {
    // Si la date est après "ce soir minuit", on bloque
    return current && current > dayjs().endOf('day');
  };
  // --------------------------------------------------

  const onFinish = async (values) => {
    setLoadingSubmit(true);
    setSuccessMessage(null); 

    try {
      const selectedPerson = assignedPersons?.data?.find(p => p.id === values.personId);
      const personName = selectedPerson 
          ? `${selectedPerson.firstname} ${selectedPerson.lastname}` 
          : "Inconnu";

      const formattedProducts = values.products?.map(prodId => ({
          productId: prodId,
          potential: "A"
      })) || [];

      const reportData = {
        personId: values.personId,
        date: values.date.format('YYYY-MM-DD HH:mm'),
        duration: String(values.duration || 10),
        note: values.notes || "Rapport Web Urgence",
        status: "Web Urgence", 
        productIds: values.products || [],
        reportProducts: formattedProducts, 
        reportSamples: values.samples?.map(s => ({
            sampleId: s.sampleId,
            quantity: s.quantity
        })) || []
      };

      await dispatch(addRapport(reportData)).unwrap();
      
      setSuccessMessage(`Le rapport pour ${personName} a été envoyé avec succès !`);
      window.scrollTo(0, 0); 
      
      form.resetFields();
      form.setFieldsValue({ duration: 10, date: null });
    } catch (error) {
      const serverMsg = error.message || (typeof error === 'object' ? JSON.stringify(error) : "Erreur inconnue");
      message.error(`Erreur: ${serverMsg}`);
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handleLogout = () => {
    dispatch(clearToken());
    history.push("/sign-in");
  };

  const filterOption = (input, option) => {
      if (!option || !option.children) return false;
      const label = String(option.children).toLowerCase();
      return label.includes(input.toLowerCase());
  };

  return (
    <Layout style={{ minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
      <Header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px', background: '#001529' }}>
        <div style={{ color: 'white', fontWeight: 'bold', fontSize: '18px' }}>Baby World - Secours</div>
        <Button type="text" icon={<LogoutOutlined />} style={{ color: 'white' }} onClick={handleLogout}>Déconnexion</Button>
      </Header>
      
      <Content style={{ padding: '20px', maxWidth: '800px', margin: '0 auto', width: '100%' }}>
        
        {successMessage && (
            <Alert
                message="Succès"
                description={successMessage}
                type="success"
                showIcon
                closable
                onClose={() => setSuccessMessage(null)}
                style={{ marginBottom: 20, border: '1px solid #b7eb8f', backgroundColor: '#f6ffed' }}
            />
        )}

        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <Title level={4}>Mode Urgence Web</Title>
          <Text type="secondary">Connecté en tant que {user?.firstName} {user?.lastName}</Text>
        </div>

        <Card bordered={false} style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <Form form={form} layout="vertical" onFinish={onFinish} initialValues={{ type: 1, duration: 10 }}>
            
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
              <Button type={personType === 1 ? 'primary' : 'default'} icon={<MedicineBoxOutlined />} onClick={() => { setPersonType(1); form.setFieldsValue({ personId: null }); }} block>Médecin</Button>
              <Button type={personType === 2 ? 'primary' : 'default'} icon={<ShopOutlined />} onClick={() => { setPersonType(2); form.setFieldsValue({ personId: null }); }} block>Pharmacie</Button>
            </div>

            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item label={personType === 1 ? "Médecin" : "Pharmacie"} name="personId" rules={[{ required: true, message: 'Requis' }]}>
                    <Select 
                        showSearch 
                        placeholder="Rechercher..." 
                        loading={loadingPersons} 
                        optionFilterProp="children" 
                        filterOption={filterOption}
                    >
                        {assignedPersons?.data?.map(p => {
                            let label = `${p.firstname || ''} ${p.lastname || ''}`;
                            if (personType === 2 && p.pharmacien?.pharmacyName) {
                                label += ` (${p.pharmacien.pharmacyName})`;
                            }
                            const specialty = p.doctor?.specialty?.name || p.doctor?.specialty?.abreviation || '';
                            const region = p.region?.name || p.address?.region?.name || p.government?.name || '';
                            let details = '';
                            if (specialty) details += ` - ${specialty}`;
                            if (region) details += ` - ${region}`;

                            return (
                                <Option key={p.id} value={p.id}>
                                    {(label + details).trim() || `ID: ${p.id}`}
                                </Option>
                            );
                        })}
                    </Select>
                    </Form.Item>
                </Col>
                <Col span={12}>
                     <Form.Item label="Date et Heure" name="date" rules={[{ required: true, message: 'Requis' }]}>
                        {/* --- 3. APPLICATION DE disabledDate --- */}
                        <DatePicker 
                            showTime 
                            format="DD/MM/YYYY HH:mm" 
                            style={{ width: '100%' }} 
                            disabledDate={disabledDate} 
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Form.Item label="Durée (minutes)" name="duration" rules={[{ required: true }]}>
                <InputNumber min={1} max={120} style={{ width: '100%' }} />
            </Form.Item>

            <Divider orientation="left">Produits & Échantillons</Divider>

            <Form.Item label="Produits présentés" name="products">
                <Select 
                    mode="multiple" 
                    placeholder="Sélectionnez les produits..."
                    loading={loadingItems}
                    filterOption={filterOption}
                >
                    {productsList.map(prod => (
                        <Option key={prod.id} value={prod.id}>{prod.name}</Option>
                    ))}
                </Select>
            </Form.Item>

            <Text strong style={{display:'block', marginBottom: 8}}>Échantillons offerts :</Text>
            <Form.List name="samples">
                {(fields, { add, remove }) => (
                <>
                    {fields.map(({ key, name, ...restField }) => (
                    <div key={key} style={{ display: 'flex', gap: '10px', marginBottom: 8, alignItems: 'center' }}>
                        <Form.Item {...restField} name={[name, 'sampleId']} style={{ flex: 1, margin: 0 }} rules={[{ required: true, message: 'Requis' }]}>
                            <Select placeholder="Échantillon" showSearch filterOption={filterOption}>
                                {samplesList.map(s => <Option key={s.id} value={s.id}>{s.name}</Option>)}
                            </Select>
                        </Form.Item>
                        <Form.Item {...restField} name={[name, 'quantity']} style={{ width: 100, margin: 0 }} rules={[{ required: true, message: 'Qté' }]}>
                            <InputNumber min={1} placeholder="Qté" />
                        </Form.Item>
                        <DeleteOutlined onClick={() => remove(name)} style={{ color: 'red', cursor: 'pointer' }} />
                    </div>
                    ))}
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={{ marginTop: 10 }} disabled={loadingItems}>
                        Ajouter un échantillon
                    </Button>
                </>
                )}
            </Form.List>

            <Divider />

            <Form.Item label="Note / Commentaire" name="notes">
              <Input.TextArea rows={3} placeholder="Détails de la visite..." />
            </Form.Item>

            <Button type="primary" htmlType="submit" loading={loadingSubmit} block size="large" style={{ marginTop: '10px', height: '50px', fontSize: '16px' }}>
              ENVOYER LE RAPPORT
            </Button>

          </Form>
        </Card>
      </Content>
    </Layout>
  );
};

export default EmergencyReportPage;